/** Shared formatting helpers for truthful ride details and receipts. */
export const toMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (typeof value.toDate === 'function') return value.toDate().getTime();
  if (typeof value === 'number') return value;
  if (value.seconds != null) return Number(value.seconds) * 1000;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getFareSnapshot = (ride) => {
  const snapshot = ride?.fareSnapshot;
  if (!snapshot || snapshot.total == null) return null;
  const total = Number(snapshot.total);
  if (!Number.isFinite(total)) return null;
  return {
    total,
    baseFare: Number.isFinite(Number(snapshot.baseFare)) ? Number(snapshot.baseFare) : null,
    perKm: Number.isFinite(Number(snapshot.perKm)) ? Number(snapshot.perKm) : null,
    minimum: Number.isFinite(Number(snapshot.minimum)) ? Number(snapshot.minimum) : null,
    subtotal: Number.isFinite(Number(snapshot.subtotal)) ? Number(snapshot.subtotal) : null,
    distanceCharge: Number.isFinite(Number(snapshot.distanceCharge)) ? Number(snapshot.distanceCharge) : null,
    minimumAdjustment: Number.isFinite(Number(snapshot.minimumAdjustment)) ? Number(snapshot.minimumAdjustment) : null,
    appliedMinimum: snapshot.appliedMinimum === true,
    tip: Number.isFinite(Number(snapshot.tip)) ? Number(snapshot.tip) : 0,
    distanceKm: Number.isFinite(Number(snapshot.distanceKm)) ? Number(snapshot.distanceKm) : null,
    isNightFare: snapshot.isNightFare === true,
    nightSurcharge: Number.isFinite(Number(snapshot.nightSurcharge)) ? Number(snapshot.nightSurcharge) : 0,
    isRainFare: snapshot.isRainFare === true,
    rainSurcharge: Number.isFinite(Number(snapshot.rainSurcharge)) ? Number(snapshot.rainSurcharge) : 0,
    // These were written to the snapshot but never read here, so receipts could
    // not show them (the cancellation fee row never appeared).
    cancellationFee: Number.isFinite(Number(snapshot.cancellationFee)) ? Number(snapshot.cancellationFee) : 0,
    waitingCharge: Number.isFinite(Number(snapshot.waitingCharge)) ? Number(snapshot.waitingCharge) : 0,
    waitingMinutes: Number.isFinite(Number(snapshot.waitingMinutes)) ? Number(snapshot.waitingMinutes) : 0,
  };
};

// Passenger-facing ride distance = the booked ROUTE distance. It matches the
// fare and every ride-detail screen. The raw GPS trail (actualDistanceKm) is a
// backend audit signal only — it over-counts real trips (it includes the
// driver's drive to the pickup) and under-counts on GPS dropouts.
export const getRideDistanceKm = (ride) => {
  const snapshot = getFareSnapshot(ride);
  const route = snapshot?.distanceKm ?? ride?.distanceKm;
  if (Number.isFinite(Number(route)) && Number(route) > 0) return Number(route);
  const actual = Number(ride?.actualDistanceKm);
  return Number.isFinite(actual) && actual > 0 ? actual : null;
};

export const getRideDurationMin = (ride) => {
  if (ride?.status === 'completed') {
    const actual = Number(ride?.actualDurationMin);
    return Number.isFinite(actual) && actual > 0 ? actual : null;
  }
  if (Number.isFinite(Number(ride?.actualDurationMin)) && Number(ride.actualDurationMin) >= 0) return Number(ride.actualDurationMin);
  const raw = ride?.durationMin;
  if (Number.isFinite(Number(raw)) && Number(raw) > 0) return Number(raw);
  const match = String(ride?.duration || '').match(/[\d.]+/);
  return match && Number(match[0]) > 0 ? Number(match[0]) : null;
};

// Fare distance is the exact route distance used by the backend calculation.
// It is safe to show when an actual GPS trail is unavailable because it is not
// presented as a measured GPS distance.
export const getFareDistanceKm = (ride) => {
  const snapshot = getFareSnapshot(ride);
  const value = snapshot?.distanceKm;
  return Number.isFinite(Number(value)) && Number(value) > 0 ? Number(value) : null;
};

export const formatMoney = (value) => {
  // Number(null) is 0 (a finite value), so a missing fare must be caught before
  // the Number() call — otherwise an unrecorded fare renders as "₹0" (looks
  // like a free ride) instead of "—" (unknown).
  if (value == null || value === '') return '—';
  const n = Number(value);
  return Number.isFinite(n) ? `₹${n.toFixed(0)}` : '—';
};
